import { useMemo } from "react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"

export interface EditableSelectOption {
  label: string
  value: string
}

interface EditableSelectFieldProps {
  value?: string
  placeholder: string
  options: ReadonlyArray<EditableSelectOption>
  onChange: (value: string) => void
  editableFieldAttr: Record<string, string>
  focusTargetAttr: Record<string, string>
  disabled?: boolean
  includeClearOption?: boolean
  triggerClassName?: string
  extraOptions?: ReadonlyArray<EditableSelectOption>
}

const DEFAULT_TRIGGER_CLASSES =
  "flex h-9 w-full items-center justify-between rounded border border-neutral-200 bg-white px-2 text-sm font-semibold text-neutral-900 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"

export const EditableSelectField = ({
  value,
  placeholder,
  options,
  onChange,
  editableFieldAttr,
  focusTargetAttr,
  disabled,
  includeClearOption = true,
  triggerClassName,
  extraOptions
}: EditableSelectFieldProps): JSX.Element => {
  const triggerClasses = cn(DEFAULT_TRIGGER_CLASSES, triggerClassName)
  const normalizedOptions = options.filter((option) => option.value.trim() !== "")

  // Compute display label ourselves to avoid Radix SelectItemText portal duplication
  // when options change asynchronously
  const displayLabel = useMemo(() => {
    if (!value || value === "__clear") return undefined
    const allOptions = extraOptions ? [...normalizedOptions, ...extraOptions] : normalizedOptions
    return allOptions.find((opt) => opt.value === value)?.label ?? value
  }, [value, normalizedOptions, extraOptions])

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger {...editableFieldAttr} {...focusTargetAttr} className={triggerClasses}>
        <SelectValue placeholder={placeholder}>
          {displayLabel}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-60">
        {includeClearOption && (
          <SelectItem value="__clear" className="cursor-pointer">
            No value
          </SelectItem>
        )}
        {normalizedOptions.map((option) => (
          <SelectItem key={option.value} value={option.value} className="cursor-pointer">
            {option.label}
          </SelectItem>
        ))}
        {extraOptions?.map((option) => (
          <SelectItem key={option.value} value={option.value} className="cursor-pointer">
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
